import { useState, useCallback } from 'react'

/**
 * Simple localStorage hook
 * @param {string} key - localStorage key
 * @param {*} initialValue - default value
 */
const useLocalStorage = (key, initialValue = null) => {
    // Get initial value from localStorage
    const [value, setValue] = useState(() => {
        try {
            const item = localStorage.getItem(key)
            return item ? JSON.parse(item) : initialValue
        } catch {
            return initialValue
        }
    })

    // Set value to localStorage
    const setStoredValue = useCallback(
        (newValue) => {
            try {
                setValue(newValue)
                localStorage.setItem(key, JSON.stringify(newValue))
            } catch (error) {
                console.error('Error saving to localStorage:', error)
            }
        },
        [key]
    )

    // Delete value from localStorage
    const deleteValue = useCallback(() => {
        try {
            setValue(initialValue)
            localStorage.removeItem(key)
        } catch (error) {
            console.error('Error deleting from localStorage:', error)
        }
    }, [key, initialValue])

    // Get fresh value from localStorage
    const getValue = useCallback(() => {
        try {
            const item = localStorage.getItem(key)
            return item ? JSON.parse(item) : initialValue
        } catch {
            return initialValue
        }
    }, [key, initialValue])

    return {
        value,
        setValue: setStoredValue,
        deleteValue,
        getValue
    }
}

export default useLocalStorage
